<script setup lang="ts">
import mapboxgl, { type GeoJSONSource, type Map as MapboxMap, type PaddingOptions } from 'mapbox-gl';
import { onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { categories } from '@/data/categories';
import type { Route } from '@/data/directions';
import type { LngLat, MapLocation } from '@/data/types';

const props = defineProps<{
  locations: MapLocation[];
  /** Only pins in this category are shown; null shows every pin. */
  activeCategory: string | null;
  selected: MapLocation | null;
  /** Map area hidden behind the side panel / bottom sheet, so camera moves center in the visible part. */
  padding: PaddingOptions;
  /** Bumped by the parent to request a camera move; the latest value wins. */
  flyTarget: { center: LngLat; zoom: number; seq: number } | null;
  initialCenter: LngLat;
  initialZoom: number;
  /** Directions view: the route line and where it starts (null while there's no route). */
  route: Route | null;
  routeOrigin: LngLat | null;
  /** True in the directions view; the camera fits the route instead of flying to the pin. */
  routing: boolean;
}>();

const emit = defineEmits<{
  select: [slug: string];
  /** A tap/click on the map that didn't hit a pin (mobile uses it to collapse the sheet). */
  backgroundClick: [];
  /** Fired after every camera move with the center of the visible (padded) area. */
  view: [center: LngLat, zoom: number];
}>();

const container = ref<HTMLDivElement>();
const map = shallowRef<MapboxMap>();
const ready = ref(false);

const SOURCE = 'locations';
const LAYER = 'locations';
const SELECTED_LAYER = 'locations-selected';
const ROUTE_SOURCE = 'route';
const ORIGIN_SOURCE = 'route-origin';

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

function toGeoJSON(locations: MapLocation[]): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: locations.map((l) => ({
      type: 'Feature',
      id: l.id,
      geometry: { type: 'Point', coordinates: l.lngLat },
      properties: { slug: l.slug, title: l.title, category: l.category, lat: l.lngLat[1] },
    })),
  };
}

function loadImage(m: MapboxMap, url: string): Promise<HTMLImageElement | ImageBitmap | ImageData> {
  return new Promise((resolve, reject) => {
    m.loadImage(url, (err, image) => (err || !image ? reject(err ?? new Error(`no image ${url}`)) : resolve(image)));
  });
}

// Which URL each pin image was loaded from, so a category list update (e.g. Directus pins
// replacing the built-in ones) only reloads pins that actually changed.
const loadedPins = new Map<string, string>();

async function syncPinImages(m: MapboxMap) {
  await Promise.all(
    categories.value.map(async (c) => {
      if (loadedPins.get(c.key) === c.pinUrl) return;
      loadedPins.set(c.key, c.pinUrl);
      try {
        // Pins are 100x126 @2x artwork.
        const image = await loadImage(m, c.pinUrl);
        if (loadedPins.get(c.key) !== c.pinUrl) return; // superseded while loading
        if (m.hasImage(c.key)) m.removeImage(c.key);
        m.addImage(c.key, image, { pixelRatio: 2 });
      } catch (err) {
        console.warn(`[maps] pin for ${c.key} failed to load from ${c.pinUrl}`, err);
      }
    }),
  );
}

function routeData(route: Route | null): GeoJSON.Feature | GeoJSON.FeatureCollection {
  return route ? { type: 'Feature', geometry: route.geometry, properties: { mode: route.mode } } : EMPTY;
}

function originData(lngLat: LngLat | null): GeoJSON.Feature | GeoJSON.FeatureCollection {
  return lngLat ? { type: 'Feature', geometry: { type: 'Point', coordinates: [lngLat[0], lngLat[1]] }, properties: {} } : EMPTY;
}

function addRouteLayers(m: MapboxMap) {
  m.addSource(ROUTE_SOURCE, { type: 'geojson', data: routeData(props.route) });
  m.addSource(ORIGIN_SOURCE, { type: 'geojson', data: originData(props.routeOrigin) });
  m.addLayer({
    id: 'route-casing',
    type: 'line',
    source: ROUTE_SOURCE,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 13, 6, 18, 12] },
  });
  m.addLayer({
    id: 'route-line',
    type: 'line',
    source: ROUTE_SOURCE,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#283faf',
      'line-width': ['interpolate', ['linear'], ['zoom'], 13, 3.5, 18, 7],
    },
  });
  m.addLayer({
    id: 'route-origin',
    type: 'circle',
    source: ORIGIN_SOURCE,
    paint: {
      'circle-radius': 7,
      'circle-color': '#ffffff',
      'circle-stroke-color': '#283faf',
      'circle-stroke-width': 4,
    },
  });
}

/** Walking routes draw dotted, driving solid. */
function styleRoute(m: MapboxMap) {
  m.setPaintProperty('route-line', 'line-dasharray', props.route?.mode === 'walking' ? [0.1, 1.8] : [1, 0]);
}

function fitRoute() {
  const m = map.value;
  const coords = props.route?.geometry.coordinates;
  if (!m || !coords?.length) return;
  const bounds = new mapboxgl.LngLatBounds(coords[0] as [number, number], coords[0] as [number, number]);
  for (const c of coords) bounds.extend(c as [number, number]);
  const p = props.padding;
  const extra = 48;
  m.fitBounds(bounds, {
    padding: {
      top: (p.top ?? 0) + extra,
      bottom: (p.bottom ?? 0) + extra,
      left: (p.left ?? 0) + extra,
      right: (p.right ?? 0) + extra,
    },
    maxZoom: 18,
    essential: false,
  });
}

function addLayers(m: MapboxMap) {
  addRouteLayers(m);
  m.addSource(SOURCE, { type: 'geojson', data: toGeoJSON(props.locations) });

  // Smaller at campus zoom so pins don't bury building names; full size only when close in.
  const iconSize: mapboxgl.ExpressionSpecification = ['interpolate', ['linear'], ['zoom'], 13, 0.4, 16, 0.55, 18, 0.85];
  m.addLayer({
    id: LAYER,
    type: 'symbol',
    source: SOURCE,
    layout: {
      'icon-image': ['get', 'category'],
      'icon-size': iconSize,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      // Pins further south are lower on screen, so draw them on top, like a stack of cards.
      'symbol-sort-key': ['-', 0, ['get', 'lat']],
    },
  });
  m.addLayer({
    id: SELECTED_LAYER,
    type: 'symbol',
    source: SOURCE,
    filter: ['==', ['get', 'slug'], ''],
    layout: {
      'icon-image': ['get', 'category'],
      'icon-size': ['interpolate', ['linear'], ['zoom'], 13, 0.6, 16, 0.8, 18, 1.1],
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });

  const hoverPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: [0, -40], className: 'pin-tooltip' });

  for (const layer of [LAYER, SELECTED_LAYER]) {
    m.on('click', layer, (e) => {
      const slug = e.features?.[0]?.properties?.slug;
      if (typeof slug === 'string') emit('select', slug);
    });
    m.on('mouseenter', layer, (e) => {
      m.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0];
      if (f?.geometry.type === 'Point' && typeof f.properties?.title === 'string') {
        hoverPopup
          .setLngLat(f.geometry.coordinates as [number, number])
          .setText(f.properties.title)
          .addTo(m);
      }
    });
    m.on('mouseleave', layer, () => {
      m.getCanvas().style.cursor = '';
      hoverPopup.remove();
    });
  }
}

function applyFilters() {
  const m = map.value;
  if (!m || !ready.value) return;
  const category = props.selected?.category ?? props.activeCategory;
  m.setFilter(LAYER, category ? ['==', ['get', 'category'], category] : null);
  m.setFilter(SELECTED_LAYER, ['==', ['get', 'slug'], props.selected?.slug ?? '']);
}

function flyTo(center: LngLat, zoom?: number) {
  map.value?.flyTo({
    center,
    zoom: zoom ?? Math.max(map.value.getZoom(), 17),
    padding: props.padding,
    essential: false, // honor prefers-reduced-motion
  });
}

onMounted(() => {
  if (!container.value) return;
  const m = new mapboxgl.Map({
    container: container.value,
    style: import.meta.env.VITE_MAPBOX_STYLE,
    center: props.initialCenter,
    zoom: props.initialZoom,
    minZoom: 10,
    cooperativeGestures: false,
    attributionControl: true,
  });
  map.value = m;
  if (import.meta.env.DEV) (window as unknown as { __map: MapboxMap }).__map = m;

  m.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
  m.addControl(
    new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
    }),
    'top-right',
  );
  m.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }), 'bottom-right');

  m.on('error', (e) => {
    // The custom style references a missing source layer ("buildingsCampusMaster"); that's a
    // Mapbox Studio problem and shouldn't surface to users, so just log.
    console.warn('[maps] mapbox error:', e.error?.message ?? e);
  });

  // mapbox-gl only resizes on window 'resize'. On iOS Safari the container can change size
  // without one (toolbar/viewport changes), leaving the canvas short with a blank band
  // below it, so watch the container directly.
  resizeObserver = new ResizeObserver(() => m.resize());
  resizeObserver.observe(container.value);
  window.visualViewport?.addEventListener('resize', onViewportResize);

  m.on('click', (e) => {
    const layers = [LAYER, SELECTED_LAYER].filter((id) => m.getLayer(id));
    const hits = layers.length ? m.queryRenderedFeatures(e.point, { layers }) : [];
    if (!hits.length) emit('backgroundClick');
  });

  m.on('moveend', () => {
    const c = m.getCenter();
    emit('view', [c.lng, c.lat], m.getZoom());
  });

  m.on('load', async () => {
    m.resize();
    await syncPinImages(m);
    addLayers(m);
    ready.value = true;
    applyFilters();
    styleRoute(m);
    if (props.route) fitRoute();
    else if (props.selected) flyTo(props.selected.lngLat);
  });
});

let resizeObserver: ResizeObserver | undefined;
function onViewportResize() {
  map.value?.resize();
}

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  window.visualViewport?.removeEventListener('resize', onViewportResize);
  map.value?.remove();
});

watch(
  () => props.locations,
  (locations) => {
    const source = map.value?.getSource<GeoJSONSource>(SOURCE);
    source?.setData(toGeoJSON(locations));
  },
);

watch(() => [props.activeCategory, props.selected?.slug], applyFilters);

watch(categories, () => {
  if (map.value && ready.value) syncPinImages(map.value);
});

watch(
  () => props.selected?.slug,
  () => {
    if (ready.value && props.selected && !props.routing) flyTo(props.selected.lngLat);
  },
);

watch(
  () => props.route,
  (route) => {
    const m = map.value;
    m?.getSource<GeoJSONSource>(ROUTE_SOURCE)?.setData(routeData(route));
    if (m && ready.value) {
      styleRoute(m);
      if (route) fitRoute();
      // Start point cleared ("Change"): come back to the destination instead of staying on
      // the old route's extent.
      else if (props.routing && props.selected) flyTo(props.selected.lngLat);
    }
  },
);

watch(
  () => props.routeOrigin,
  (lngLat) => map.value?.getSource<GeoJSONSource>(ORIGIN_SOURCE)?.setData(originData(lngLat)),
);

watch(
  () => props.flyTarget?.seq,
  () => {
    if (props.flyTarget) flyTo(props.flyTarget.center, props.flyTarget.zoom);
  },
);

watch(
  () => props.padding,
  (padding) => map.value?.setPadding(padding),
  { deep: true },
);
</script>

<template>
  <div ref="container" class="map-canvas" role="region" aria-label="Campus map" />
</template>

<style scoped>
.map-canvas {
  position: absolute;
  inset: 0;
}

/* Mapbox's terms require the logo and attribution to stay visible: move the bottom control
   corners out from under the side panel (desktop) and above the bottom sheet (mobile).
   The insets are set on .stage by MapApp from the same numbers as the camera padding. */
:deep(.mapboxgl-ctrl-bottom-left) {
  left: var(--map-inset-left, 0px);
  bottom: var(--map-inset-bottom, 0px);
}

:deep(.mapboxgl-ctrl-bottom-right) {
  bottom: var(--map-inset-bottom, 0px);
}

:deep(.pin-tooltip .mapboxgl-popup-content) {
  padding: 6px 10px;
  font: 600 14px/1.2 var(--font);
  border-radius: 6px;
  box-shadow: var(--shadow);
  pointer-events: none;
}
</style>
